import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from '@/hooks/useDebounce'
import { captureUnexpectedError } from '@/utils/error'
import { BPMN_TIMING } from '../constants'

export type BpmnSaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'

interface UseBpmnAutosaveProps {
	initialXml: string | null
	onSave?: (xml: string) => Promise<void>
	/** When this changes (e.g. snapshot version), reset debounce/queue so saves do not cross versions */
	autosaveContextKey?: string
}

interface UseBpmnAutosaveReturn {
	saveStatus: BpmnSaveStatus
	isSaveInFlight: boolean
	isSaveStalled: boolean
	triggerSave: (xml: string) => void
	performManualSave: (xml: string) => Promise<void>
	unsavedChangesRef: MutableRefObject<boolean>
}

/**
 * Hook for BPMN editor autosave with debounce.
 * Call triggerSave(xml) when the modeler's commandStack.changed fires.
 *
 * Unmount / tab close: debounced timers alone can drop edits (SCRUM-304). We flush
 * pending XML via onSave in effect cleanup when there are unsaved changes (best-effort;
 * browser refresh may still abort the request — beforeunload warns the user).
 */
export function useBpmnAutosave({
	initialXml,
	onSave,
	autosaveContextKey,
}: UseBpmnAutosaveProps): UseBpmnAutosaveReturn {
	const stallTimeoutSeconds = String(BPMN_TIMING.AUTOSAVE_STALL_TIMEOUT_MS / 1000)
	const [currentXml, setCurrentXml] = useState<string>(initialXml || '')
	const [saveStatus, setSaveStatus] = useState<BpmnSaveStatus>('idle')
	const [isSaveInFlight, setIsSaveInFlight] = useState(false)
	const [isSaveStalled, setIsSaveStalled] = useState(false)

	const isSavingRef = useRef(false)
	const saveQueueRef = useRef<string | null>(null)
	const lastSavedXmlRef = useRef<string | null>(initialXml || null)
	const hasUnsavedChanges = useRef(false)
	const hasInitializedRef = useRef(false)
	const saveTimeoutRef = useRef<number | null>(null)
	const hasCapturedTimeoutRef = useRef(false)
	const mountedRef = useRef(true)
	const latestXmlRef = useRef<string>(initialXml || '')
	const onSaveRef = useRef(onSave)
	const prevAutosaveContextKeyRef = useRef<string | null>(null)

	useEffect(() => {
		onSaveRef.current = onSave
	}, [onSave])

	const clearStallTimer = useCallback(() => {
		if (saveTimeoutRef.current !== null) {
			window.clearTimeout(saveTimeoutRef.current)
			saveTimeoutRef.current = null
		}
	}, [])

	const resetStallState = useCallback(() => {
		clearStallTimer()
		hasCapturedTimeoutRef.current = false
		setIsSaveStalled(false)
	}, [clearStallTimer])

	const startStallTimer = useCallback(() => {
		clearStallTimer()
		saveTimeoutRef.current = window.setTimeout(() => {
			setIsSaveStalled(true)
			if (hasCapturedTimeoutRef.current) return

			hasCapturedTimeoutRef.current = true
			const timeoutError = new Error(`BPMN autosave exceeded ${stallTimeoutSeconds} seconds`)

			console.error(`BPMN autosave stalled for more than ${stallTimeoutSeconds} seconds`)
			captureUnexpectedError(timeoutError, {
				tags: { feature: 'bpmn-autosave', timeout_seconds: stallTimeoutSeconds },
				level: 'error',
			})
		}, BPMN_TIMING.AUTOSAVE_STALL_TIMEOUT_MS)
	}, [clearStallTimer, stallTimeoutSeconds])

	useEffect(() => {
		const contextKey = autosaveContextKey ?? ''
		const contextChanged =
			prevAutosaveContextKeyRef.current !== null && prevAutosaveContextKeyRef.current !== contextKey
		prevAutosaveContextKeyRef.current = contextKey

		if (!initialXml) {
			saveQueueRef.current = null
			isSavingRef.current = false
			hasUnsavedChanges.current = false
			setIsSaveInFlight(false)
			lastSavedXmlRef.current = null
			hasInitializedRef.current = false
			setCurrentXml('')
			latestXmlRef.current = ''
			resetStallState()
			setSaveStatus('idle')
			return
		}

		if (contextChanged) {
			saveQueueRef.current = null
			isSavingRef.current = false
			hasUnsavedChanges.current = false
			lastSavedXmlRef.current = initialXml
			setCurrentXml(initialXml)
			latestXmlRef.current = initialXml
			resetStallState()
			hasInitializedRef.current = true
			setSaveStatus('saved')
			setIsSaveInFlight(false)
			return
		}

		if (isSavingRef.current) {
			return
		}

		if (hasUnsavedChanges.current && initialXml !== latestXmlRef.current) {
			return
		}

		saveQueueRef.current = null
		isSavingRef.current = false
		hasUnsavedChanges.current = false
		lastSavedXmlRef.current = initialXml
		setCurrentXml(initialXml)
		latestXmlRef.current = initialXml
		resetStallState()
		hasInitializedRef.current = true
		setSaveStatus('saved')
		setIsSaveInFlight(false)
	}, [initialXml, autosaveContextKey, resetStallState])

	useEffect(() => {
		if (saveStatus === 'saving') {
			startStallTimer()
			return
		}

		resetStallState()
	}, [saveStatus, startStallTimer, resetStallState])

	useEffect(() => () => clearStallTimer(), [clearStallTimer])

	const debouncedXml = useDebounce(currentXml, BPMN_TIMING.AUTOSAVE_DEBOUNCE_MS, (a, b) => a === b)

	const performSave = useCallback(
		async (xml: string) => {
			if (!onSave || !xml.trim()) return

			isSavingRef.current = true
			if (mountedRef.current) {
				setIsSaveInFlight(true)
				setSaveStatus('saving')
			}

			try {
				await onSave(xml)
				lastSavedXmlRef.current = xml
				const syncedWithEditor = latestXmlRef.current === xml
				hasUnsavedChanges.current = !syncedWithEditor
				if (mountedRef.current) {
					setIsSaveInFlight(false)
					setSaveStatus(syncedWithEditor ? 'saved' : 'dirty')
					setIsSaveStalled(false)
				}

				// Process queued save if any
				if (saveQueueRef.current !== null) {
					const queued = saveQueueRef.current
					saveQueueRef.current = null
					if (queued === xml) {
						// Drop exact duplicate queued saves; the successful write already persisted this XML.
						setTimeout(() => {
							isSavingRef.current = false
						}, BPMN_TIMING.SAVE_LOCK_DELAY_MS)
						return
					}
					setTimeout(() => {
						void performSave(queued)
					}, BPMN_TIMING.SAVE_LOCK_DELAY_MS)
					return
				}

				setTimeout(() => {
					isSavingRef.current = false
				}, BPMN_TIMING.SAVE_LOCK_DELAY_MS)
			} catch (err) {
				console.error('Error autosaving BPMN:', err)
				if (mountedRef.current) {
					setIsSaveInFlight(false)
					setSaveStatus('error')
					setIsSaveStalled(false)
				}
				isSavingRef.current = false
				saveQueueRef.current = null
			}
		},
		[onSave]
	)

	// Autosave effect - triggers when debounced XML changes
	useEffect(() => {
		if (!onSave) return
		if (!hasInitializedRef.current) return
		if (!hasUnsavedChanges.current) return

		const xmlChanged = lastSavedXmlRef.current === null || debouncedXml !== lastSavedXmlRef.current

		if (!xmlChanged) {
			return
		}

		if (!debouncedXml.trim()) return

		if (isSavingRef.current) {
			saveQueueRef.current = debouncedXml
			return
		}

		void performSave(debouncedXml)
	}, [debouncedXml, onSave, performSave])

	const triggerSave = useCallback((xml: string) => {
		if (!xml.trim()) return
		if (!hasInitializedRef.current) {
			hasInitializedRef.current = true
		}
		latestXmlRef.current = xml
		setCurrentXml(xml)
		hasUnsavedChanges.current = true
		setSaveStatus((currentStatus) => (currentStatus === 'saving' ? currentStatus : 'dirty'))
	}, [])

	const performManualSave = useCallback(
		async (xml: string) => {
			if (!xml.trim() || !onSave) return
			await performSave(xml)
		},
		[onSave, performSave]
	)

	// Mount / unmount: avoid setState after unmount; best-effort flush pending edits.
	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false

			const save = onSaveRef.current
			if (!save) return
			if (!hasInitializedRef.current) return
			if (isSavingRef.current) return
			if (!hasUnsavedChanges.current) return

			const xml = latestXmlRef.current
			if (!xml.trim()) return
			if (lastSavedXmlRef.current !== null && xml === lastSavedXmlRef.current) return

			void save(xml)
				.then(() => {
					hasUnsavedChanges.current = false
					lastSavedXmlRef.current = xml
				})
				.catch((err) => {
					console.error('BPMN flush on unmount failed:', err)
				})
		}
	}, [])

	return {
		saveStatus,
		isSaveInFlight,
		isSaveStalled,
		triggerSave,
		performManualSave,
		unsavedChangesRef: hasUnsavedChanges,
	}
}
